import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowUpRight, Sparkles, LineChart, Download, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import usePageMeta from "../hooks/usePageMeta";

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  usePageMeta(
    "Trackr — The honest way to track every job application",
    "Log every application, track interviews, spot follow-ups, and export to CSV, Excel or PDF. Free for 25 applications; Pro from $6/mo."
  );

  return (
    <div className="min-h-screen bg-[#F4F1EE] text-[#1A362D] font-body">
      {/* Header */}
      <header className="glass sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#1A362D] flex items-center justify-center">
              <span className="text-[#D99C3D] font-display font-bold">T</span>
            </div>
            <span className="font-display text-xl font-semibold tracking-tight">Trackr</span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm">
            <a href="#features" className="hover:text-[#C65D47] transition-colors">Features</a>
            <a href="#workflow" className="hover:text-[#C65D47] transition-colors">Workflow</a>
            <a href="#export" className="hover:text-[#C65D47] transition-colors">Export</a>
            <Link to="/blog" className="hover:text-[#C65D47] transition-colors" data-testid="landing-nav-guides-link">Guides</Link>
            <Link to="/compare" className="hover:text-[#C65D47] transition-colors" data-testid="landing-nav-compare-link">Compare</Link>
          </nav>
          <div className="flex items-center gap-3">
            {user ? (
              <Button
                onClick={() => navigate("/dashboard")}
                className="rounded-full bg-[#1A362D] hover:bg-[#2C4A40] text-white px-5"
                data-testid="go-to-dashboard-btn"
              >
                Open dashboard
              </Button>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-sm hover:text-[#C65D47] transition-colors"
                  data-testid="header-signin-link"
                >
                  Sign in
                </Link>
                <Button
                  onClick={() => navigate("/register")}
                  className="rounded-full bg-[#1A362D] hover:bg-[#2C4A40] text-white px-5"
                  data-testid="header-get-started-btn"
                >
                  Get started
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero — asymmetric bento */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8 lg:pr-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E2F0E7] text-[#1A362D] text-xs tracking-[0.2em] uppercase font-semibold">
              <Sparkles className="w-3 h-3" /> Built for the search
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight font-medium mt-6">
              The quiet, honest way to <span className="text-[#C65D47]">track every job</span> you apply to.
            </h1>
            <p className="mt-6 text-lg text-[#5C6B64] max-w-2xl leading-relaxed">
              Log the company, the role, the pay range, where you found it, and where you stand — in one calm, elegant table. Export as CSV, Excel, or PDF anytime.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button
                size="lg"
                onClick={() => navigate(user ? "/dashboard" : "/register")}
                className="rounded-full bg-[#1A362D] hover:bg-[#2C4A40] text-white px-7 h-12 text-base"
                data-testid="hero-cta-btn"
              >
                Start tracking free <ArrowUpRight className="ml-2 w-4 h-4" />
              </Button>
              <Link
                to="/login"
                className="text-sm underline-offset-4 hover:underline text-[#1A362D]"
                data-testid="hero-signin-link"
              >
                Already have an account? Sign in
              </Link>
            </div>
          </div>

          <div className="lg:col-span-4 relative">
            <div className="relative rounded-3xl overflow-hidden bg-[#1A362D] aspect-[4/5] shadow-xl shadow-black/10">
              <img
                src="https://images.unsplash.com/photo-1638802126216-dbab1180cb6f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxwZXJzb24lMjBsYXB0b3AlMjBzbWlsaW5nJTIwY2FmZXxlbnwwfHx8fDE3ODQ1MDQ5NTJ8MA&ixlib=rb-4.1.0&q=85"
                alt="Job hunter at cafe"
                className="w-full h-full object-cover mix-blend-luminosity opacity-90"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A362D]/60 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5">
                <div className="rounded-2xl bg-white/90 backdrop-blur p-4">
                  <p className="text-xs tracking-[0.2em] uppercase text-[#5C6B64]">Offer this week</p>
                  <p className="font-display text-2xl text-[#1A362D] mt-1">$142k · Remote</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bento feature grid */}
        <div id="features" className="grid grid-cols-1 md:grid-cols-6 gap-6 mt-20">
          <div className="md:col-span-4 rounded-3xl bg-white p-8 border border-[#E5E1DB]">
            <LineChart className="w-6 h-6 text-[#C65D47]" />
            <h3 className="font-display text-2xl mt-4">A table that respects your effort</h3>
            <p className="text-[#5C6B64] mt-2 leading-relaxed">
              Sort, filter, and search across company, title, source, pay range, and status. Everything you need — no clutter.
            </p>
          </div>
          <div className="md:col-span-2 rounded-3xl bg-[#1A362D] text-[#F4F1EE] p-8">
            <Download className="w-6 h-6 text-[#D99C3D]" />
            <h3 className="font-display text-2xl mt-4">Export any time</h3>
            <p className="opacity-80 mt-2">CSV. Excel. PDF. Your data, in your hands.</p>
          </div>
          <div className="md:col-span-2 rounded-3xl bg-[#FDF2DF] p-8">
            <ShieldCheck className="w-6 h-6 text-[#D99C3D]" />
            <h3 className="font-display text-2xl mt-4">Private by default</h3>
            <p className="text-[#5C6B64] mt-2">Sign in with Google or email. Only you see your list.</p>
          </div>
          <div className="md:col-span-4 rounded-3xl bg-[#E2F0E7] p-8">
            <Sparkles className="w-6 h-6 text-[#1A362D]" />
            <h3 className="font-display text-2xl mt-4">Status you can actually feel</h3>
            <p className="text-[#5C6B64] mt-2 leading-relaxed">
              Applied. Interviewing. Offer. Rejected. Track momentum without the doomscroll.
            </p>
          </div>
        </div>
        {/* FAQ — matches JSON-LD in index.html for Google rich snippets */}
        <section id="faq" className="mt-24">
          <p className="text-xs tracking-[0.2em] uppercase text-[#5C6B64] font-semibold">Frequently asked</p>
          <h2 className="font-display text-4xl sm:text-5xl mt-3 text-[#1A362D] max-w-2xl">Questions people actually ask.</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                q: "Is Trackr free?",
                a: "Yes — the Free plan lets you track up to 25 applications with the table view and CSV export. Pro unlocks unlimited applications, Kanban, analytics, Excel & PDF exports, and AI features.",
              },
              {
                q: "Can I export my job applications?",
                a: "Every plan can export to CSV. Trackr Pro also exports to Excel (.xlsx) and a beautifully-formatted PDF you can share with a coach or mentor.",
              },
              {
                q: "Does Trackr help me with follow-ups?",
                a: "Trackr flags any Applied or Interviewing role you haven't touched in 7+ days and shows a polite-nudge banner — often the difference between silence and a callback.",
              },
              {
                q: "How much does Trackr Pro cost?",
                a: "Trackr Pro is $6 per month, or $19 as a one-time lifetime unlock. Cancel any time.",
              },
              {
                q: "What AI features are included?",
                a: "Paste any job description and Trackr's AI extracts company, title, pay range and requirements to auto-fill your entry. And you can generate a tailored cover letter for each role in one click.",
              },
              {
                q: "Do you sell or share my data?",
                a: "No. Your applications are private to your account. Sign in with email or Google — either way, only you see your list.",
              },
            ].map((item) => (
              <div key={item.q} className="rounded-2xl bg-white border border-[#E5E1DB] p-6">
                <h3 className="font-display text-xl text-[#1A362D]">{item.q}</h3>
                <p className="text-[#5C6B64] mt-2 leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </section>
      </section>

      <footer className="border-t border-[#E5E1DB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-[#5C6B64]">
          <span className="font-display text-[#1A362D]">Trackr</span>
          <Link to="/blog" className="hover:text-[#C65D47] transition-colors" data-testid="landing-footer-guides-link">Job search guides</Link>
          <Link to="/compare" className="hover:text-[#C65D47] transition-colors" data-testid="landing-footer-compare-link">Compare trackers</Link>
          <span>© {new Date().getFullYear()} — Built for job seekers.</span>
        </div>
      </footer>
    </div>
  );
}